import { IOrderBasis } from '@/coreDomainsModule/entities/orderBasis';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { OrderBasis, OrderBasisCustomerAnnounceDaily } from '@/coreInfrastructureModule/axios/generated/orderBasisManage';

/**
 * OrderBasisのAdapter
 */
export class OrderBasisAdapter {
  /**
   * 発注根拠の日別内示をIAnnounceDailyに変換する
   * @param response - APIの日別内示情報
   * @returns - IAnnounceDaily配列
   */
  private static adaptCustomerAnnounceDaily = (response?: OrderBasisCustomerAnnounceDaily[]) => {
    if (!response) return [];
    return response.map((announce) => ({
      year: announce.customerForecastTargetYear,
      month: announce.customerForecastTargetMonth,
      day: announce.customerForecastTargetDay,
      amount: Number(announce.customerForecastAmount),
      manageInitiatedYearMonth: {
        year: announce.customerForecastReceivedYear,
        month: announce.customerForecastReceivedMonth,
      },
      createAt: undefined,
      updateAt: undefined,
    }));
  };
  /**
   * 取得した発注根拠からIOrderBasisを生成する
   * @param result - 取得した情報
   * @returns - IOrderBasis型の発注根拠情報
   */
  public static adapt = (result: OrderBasis): IOrderBasis => {
    return {
      orderBasisId: result.orderBasisId,
      aggregateBusinessId: result.aggregateBusinessId ?? undefined,
      snapshotExecutionDate: result.snapshotExecutionDate,
      snapshotLatestInventoryAmount: Number(result.snapshotLatestInventoryAmount),
      snapshotSupplierNumber: result.snapshotSupplierNumber,
      snapshotSupplierName: result.snapshotSupplierName,
      snapshotBusinessNumber: result.snapshotBusinessNumber,
      snapshotBusinessName: result.snapshotBusinessName,
      snapshotPackage: Number(result.snapshotPackage),
      snapshotManufacturer: result.snapshotManufacturer ?? undefined,
      snapshotMoq: result.snapshotMoq != null ? Number(result.snapshotMoq) : undefined,
      snapshotOrderPackage: result.snapshotOrderPackage != null ? Number(result.snapshotOrderPackage) : undefined,
      snapshotResponsibleUsers: result.snapshotResponsibleUsers?.map((u) => UserAccountFactory.create({ email: u.id, name: u.name })) ?? [],
      snapshotSimulationSetting: {
        useRoundDown: result.snapshotUseRoundDown,
        useCurrentMonthSalesResult: result.snapshotUseCurrentMonthSalesResult,
      },
      userAdjustedLeadTime: {
        value: result.userAdjustedLeadTime,
        unit: result.userAdjustedLeadTimeUnit,
      },
      userAdjustedStdInventoryIndex: {
        min: Number(result.userAdjustedStdInventoryIndexMin),
        max: Number(result.userAdjustedStdInventoryIndexMax),
        option: {
          unit: result.userAdjustedStdInventoryIndexUnit,
        },
      },
      createdAt: result.createdAt,
      updatedAt: result.updatedAt,
      nextMonthlyInfos: result.nextMonthlyInfos.map((nextMonthlyInfo) => ({
        orderBasisTargetYearMonth: { year: nextMonthlyInfo.orderBasisTargetYear, month: nextMonthlyInfo.orderBasisTargetMonth },
        userAdjustedCustomerForecastAmount: Number(nextMonthlyInfo.userAdjustedCustomerForecastAmount),
        snapshotCustomerForecastAmount: Number(nextMonthlyInfo.snapshotCustomerForecastAmount),
        snapshotPreInventoryAmount: Number(nextMonthlyInfo.snapshotPreInventoryAmount),
        dailyInfos:
          nextMonthlyInfo.dailyInfos?.map((nextDailyInfo) => ({
            orderBasisTargetDay: nextDailyInfo.orderBasisTargetDay,
            snapshotArrivalAmount: nextDailyInfo.snapshotArrivalAmount != null ? Number(nextDailyInfo.snapshotArrivalAmount) : undefined,
            snapshotCustomerForecastDailyAmount:
              nextDailyInfo.snapshotCustomerForecastDailyAmount != null
                ? Number(nextDailyInfo.snapshotCustomerForecastDailyAmount)
                : undefined,
          })) ?? [],
      })),
      pastMonthlyInfos: result.pastMonthlyInfos.map((pastMonthlyInfo) => ({
        orderBasisTargetYearMonth: { year: pastMonthlyInfo.orderBasisTargetYear, month: pastMonthlyInfo.orderBasisTargetMonth },
        snapshotCustomerForecastAmount: Number(pastMonthlyInfo.snapshotCustomerForecastAmount),
        snapshotSalesResultAmount: Number(pastMonthlyInfo.snapshotSalesResultAmount),
        snapshotInventoryAmount: Number(pastMonthlyInfo.snapshotInventoryAmount),
      })),
      sourceOrderInfos:
        result.sourceOrderInfos?.map((sourceOrderInfo) => ({
          sourceOrderId: sourceOrderInfo.sourceOrderId,
          snapshotOrderNote: sourceOrderInfo.snapshotOrderNote ?? undefined,
          initialOrderAmount: Number(sourceOrderInfo.initialOrderAmount),
          initialDesiredDeliveryDate: sourceOrderInfo.initialDesiredDeliveryDate,
        })) ?? [],
      customerAnnounceDaily: OrderBasisAdapter.adaptCustomerAnnounceDaily(result.customerAnnounceDaily),
    };
  };
}
